import React from 'react';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Simulator from '@omilia/chat-simulator';
import { omit } from 'lodash';

import withProjectParams from 'utils/hocs/withProjectParams';
import { userType } from 'types';
import Button from 'components/Button';
import { FixChatButton } from 'components/styled';
import * as actionsChat from 'store/services/chat/actions';
import * as selectorsChat from 'store/services/chat/selectors';
import { getUser } from 'store/services/session/selectors';

const Container = styled.div`
  input {
    border: none;
    font-size: 12px; // TODO: Remove this after we remove FOUNDATION
    margin: 0;

    &:focus {
      border: none;
      box-shadow: none;
      outline: none;
    }
  }
`;

const style = {
  display: 'flex',
  boxShadow: '0 -4px 10px 0 rgba(0, 0, 0, 0.11)',
  justifyContent: 'center',
  background: '#f0f0f0',
  maxWidth: 'calc(100vw - 46px) !important',
  maxHeight: 'calc(100vh - 46px - 42px) !important',
  flexDirection: 'column',
  boxSizing: 'border-box',
  bottom: '42px',
  left: 'inherit',
  position: 'fixed',
  right: 0,
  top: 'inherit',
  zIndex: 100,
};

// We need this in order to drop "notify"
// from props that get passed to Button
const WrappedButton = props => {
  const filteredProps = omit(props, ['notify']);

  return <Button {...filteredProps} />;
};

const ChatButton = styled(WrappedButton)`
  border-radius: 0 !important;
  bottom: 0;
  font-size: 11px;
  height: 42px;
  position: fixed;
  right: 0;
  width: 150px;
  z-index: 999;
  &&& {
    ${({ notify }) =>
      notify &&
      `
        background-color: #4CAF50;
        box-shadow: inset 0 -2px 0 0 rgba(#000000, .2);
      `}
  }

  span {
    font-size: 16px;
    margin-right: 5px;
    vertical-align: middle;
  }
`;

const mapStateToProps = state => ({
  chatState: {
    isOpen: selectorsChat.getIsOpen(state),
    isLoading: selectorsChat.getIsLoading(state),

    hasError: selectorsChat.getHasError(state),
    hasThread: selectorsChat.getHasThread(state),

    data: selectorsChat.getChat(state),
    thread: selectorsChat.getThread(state),
    errorMessage: selectorsChat.getError(state),
  },
  user: getUser(state),
});

const mapDispatchToProps = {
  stop: actionsChat.stopChat,
  start: actionsChat.startChat,
  sendMessage: actionsChat.sendMessage,
  toggleMinimizeChat: actionsChat.toggleMinimizeChat,
};

const Chat = ({
  account,
  toggleMinimizeChat,
  chatState,
  sendMessage,
  slug,
  start,
  stop,
  user,
}) => {
  const notify = chatState.hasThread && !chatState.isOpen;

  const handleStartChat = () => {
    const { isOpen, hasThread } = chatState;

    if (hasThread) {
      toggleMinimizeChat(!isOpen);

      return;
    }

    start();
  };

  const selectDataModel = dataModel => {
    sendMessage('', dataModel);
  };

  // temporal normalize
  const messages = chatState.data.thread.map(x => ({
    message: {
      utterance: x.utterance,
      timestamp: x.timestamp,
      type: x.type,
      prompt: x.prompt,
      owner: x.owner,
      data_models: x.data_models,
    },
    user,
    ...x,
  }));

  return (
    <Container>
      <FixChatButton />
      <ChatButton
        data-test="simulator-button"
        notify={notify}
        onClick={handleStartChat}
      >
        <span className="icon-bot" />
        Simulator
      </ChatButton>
      {Boolean(chatState.isOpen) && (
        <Simulator
          bounds=".main-container"
          errorMessage={chatState.errorMessage}
          hasError={chatState.hasError}
          isLoading={chatState.isLoading}
          logUri={`/${account}/${slug}/chat-logs/${chatState.data.id}`}
          messages={messages}
          minimizeChat={() => toggleMinimizeChat(!chatState.isOpen)}
          project={{ slug, account }}
          startChat={start}
          stopChat={stop}
          style={style}
          onSelectDataModel={selectDataModel}
          onSend={sendMessage}
        />
      )}
    </Container>
  );
};

Chat.propTypes = {
  account: PropTypes.string.isRequired,
  sendMessage: PropTypes.func.isRequired,
  slug: PropTypes.string.isRequired,
  start: PropTypes.func.isRequired,
  stop: PropTypes.func.isRequired,
  toggleMinimizeChat: PropTypes.func.isRequired,
  user: userType.isRequired,
  chatState: PropTypes.shape({
    data: PropTypes.shape({
      id: PropTypes.string,
      thread: PropTypes.array,
    }).isRequired,
    errorMessage: PropTypes.string.isRequired,

    hasError: PropTypes.bool.isRequired,
    hasThread: PropTypes.bool.isRequired,

    isLoading: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    thread: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
};

export default compose(
  withProjectParams,
  withProps(props => ({
    slug: props.project,
    account: props.company,
  })),
  connect(mapStateToProps, mapDispatchToProps),
)(Chat);
